import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
// [SEO] 1. Import HelmetProvider & Helmet
import { HelmetProvider, Helmet } from 'react-helmet-async';

import Header from './components/layout/Header/Header';
import Footer from './components/layout/Footer/Footer';
import HomePage from './pages/HomePage/HomePage';
import ProductListPage from './pages/ProductListPage/ProductListPage';
import ProductDetailPage from './pages/ProductDetailPage/ProductDetailPage';
import CartPage from './pages/CartPage/CartPage';
import CheckoutPage from './pages/CheckoutPage/CheckoutPage';
import AdminLoginPage from './pages/AdminLoginPage/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage/AdminDashboardPage';
import AdminProtectedRoute from './components/common/AdminProtectedRoute/AdminProtectedRoute';
import AdminBannersPage from './pages/AdminBannersPage/AdminBannersPage';
import AdminOrdersPage from './pages/AdminOrdersPage/AdminOrdersPage';
import AdminProductsPage from './pages/AdminProductsPage/AdminProductsPage';
import AdminLayout from './components/layout/AdminLayout/AdminLayout';
import AdminCategoriesPage from './pages/AdminCategoriesPage/AdminCategoriesPage';
import AdminProductForm from './pages/AdminProductForm/AdminProductForm';
import AdminOrderDetailPage from './pages/AdminOrderDetailPage/AdminOrderDetailPage';
import OrderLookupPage from './pages/OrderLookupPage/OrderLookupPage';
import AdminInventoryPage from './pages/AdminInventoryPage/AdminInventoryPage';
import AdminMediaPage from './pages/AdminMediaPage/AdminMediaPage';
import ScrollToTop from './components/common/ScrollToTop/ScrollToTop';
import StoryPage from './pages/StoryPage/StoryPage';
import AdminStoryEditor from './pages/AdminStoryEditor/AdminStoryEditor';
import RawMaterialPage from './pages/RawMaterialPage/RawMaterialPage';
import AdminRawMaterialEditor from './pages/AdminRawMaterialEditor/AdminRawMaterialEditor';
import AdminRevenuePage from './pages/AdminRevenuePage/AdminRevenuePage';
import AdminAboutPage from './pages/AdminAboutPage/AdminAboutPage';
import AboutPage from './pages/AboutPage/AboutPage';
import AdminPopupPage from './pages/AdminPopupPage/AdminPopupPage';
import AdminPromotionPage from './pages/AdminPromotionPage/AdminPromotionPage';
import ComingSoonPage from './pages/ComingSoonPage/ComingSoonPage';
import HomePopup from './components/common/HomePopup/HomePopup';
import FAQManager from './pages/admin/FAQManager/FAQManager';
import FloatingCart from './components/common/FloatingCart/FloatingCart';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function App() {
  return (
    <BrowserRouter>
      {/* [SEO] 2. Bọc toàn bộ App trong HelmetProvider */}
      <HelmetProvider>
        <ScrollToTop />
        <ToastContainer
          position="top-right"
          autoClose={3000}    
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />  
        <AppLayout />
      </HelmetProvider>
    </BrowserRouter>
  );
}

const AppLayout = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // [SEO] 3. Cấu hình SEO Mặc định (Global)
  // Đây là thông tin "Dự phòng" cho toàn bộ website
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Hương Trầm Cổ Mộc",
    "url": "https://huongtramcomoc.vn",
    "logo": "https://huongtramcomoc.vn/logo.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+84-964-472-868",
      "contactType": "customer service"
    }
  };

  return (
    <>
      {/* [SEO] 4. Meta Tags mặc định (Sẽ bị trang con ghi đè nếu có) */}
      {!isAdminRoute && (
        <Helmet>
          <title>Hương Trầm Cổ Mộc - Tinh Hoa Trầm Việt</title>
          <meta name="description" content="Chuyên cung cấp Trầm hương tự nhiên, Trà Trầm, Nụ Trầm cao cấp. Cam kết 100% tự nhiên, bảo hành mùi hương trọn đời." />
          
          {/* [QUAN TRỌNG] Thêm ảnh mặc định ở đây để Trang chủ luôn có ảnh đẹp */}
          <meta property="og:image" content="https://huongtramcomoc.vn/seo-banner.jpg" />
          <meta property="og:url" content="https://huongtramcomoc.vn" />
          
          <meta property="og:site_name" content="Hương Trầm Cổ Mộc" />
          <meta property="og:type" content="website" />
          <script type="application/ld+json">{JSON.stringify(defaultSchema)}</script>
        </Helmet>
      )}

      {!isAdminRoute && <Header />}
      <HomePopup />
        <Routes>
          {/* --- Route Người Dùng --- */}
          <Route path="/" element={<HomePage />} />
          <Route path="/san-pham" element={<ProductListPage />} />
          <Route path="/san-pham/:id" element={<ProductDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/cau-chuyen" element={<StoryPage />} />
          <Route path="/vung-nguyen-lieu" element={<RawMaterialPage />} />
          <Route path="/gio-hang" element={<CartPage />} />
          <Route path="/thanh-toan" element={<CheckoutPage />} />
          <Route path="/tra-cuu-don-hang" element={<OrderLookupPage />} />
          <Route path="/chinh-sach" element={<ComingSoonPage />} />
          <Route path="/lien-he" element={<ComingSoonPage />} />
          <Route path="/kienthuc" element={<ComingSoonPage />} /> 
          <Route path="*" element={<ComingSoonPage />} />
          
          {/* --- Route Admin --- */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          
          <Route element={<AdminProtectedRoute />}>
            <Route element={<AdminLayout />}> 
              <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
              <Route path="/admin/banners" element={<AdminBannersPage />} />
              <Route path="/admin/orders" element={<AdminOrdersPage />} />
              <Route path="/admin/products" element={<AdminProductsPage />} />
              <Route path="/admin/categories" element={<AdminCategoriesPage />} />
              <Route path="/admin/products/new" element={<AdminProductForm />} />
              <Route path="/admin/products/edit/:id" element={<AdminProductForm />} />
              <Route path="/admin/orders/:id" element={<AdminOrderDetailPage />} />
              <Route path="/admin/revenue" element={<AdminRevenuePage />} />
              <Route path="/admin/inventory" element={<AdminInventoryPage />} />
              <Route path="/admin/about" element={<AdminAboutPage />} />
              <Route path="/admin/story" element={<AdminStoryEditor />} />
              <Route path="/admin/raw-material" element={<AdminRawMaterialEditor />} />
              <Route path="/admin/popup" element={<AdminPopupPage />} />
              <Route path="/admin/faq" element={<FAQManager />} />
              <Route path="/admin/coupons" element={<AdminPromotionPage/>} />
              <Route path="/admin/media" element={<AdminMediaPage />} />
            </Route>
          </Route>
          
        </Routes>
        <FloatingCart />

      {!isAdminRoute && <Footer />}
    </>
  );
};
export default App;